import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { DataSource, Folder } from '../types';
import { fetchDataAdmissionBootstrap } from '../services/dataAdmissionService';
import { dataObjectService } from '../services/dataObjectService';
import * as sharedReportService from '../services/sharedReportService';
import type { SaveSharedReportResponse } from '../types/shared';

export interface EnrichedSharedReport extends SaveSharedReportResponse {
    dataset_name?: string;
}

interface DataAdmissionContextType {
    dataSources: DataSource[];
    folders: Folder[];
    rootFolderUUID: string | null;
    isFirstLogin: boolean;
    loading: boolean;
    isInitialLoad: boolean;
    error: string | null;
    refreshData: () => Promise<void>;
    silentRefresh: () => Promise<void>;
    setDataSources: React.Dispatch<React.SetStateAction<DataSource[]>>;
    setFolders: React.Dispatch<React.SetStateAction<Folder[]>>;
    sharedReports: EnrichedSharedReport[];
    deleteSharedReport: (reportId: string) => Promise<void>;
}

const DataAdmissionContext = createContext<DataAdmissionContextType | undefined>(undefined);

export const DataAdmissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const location = useLocation();
    const USE_BACKEND = import.meta.env.VITE_USE_BACKEND === 'true';

    const [dataSources, setDataSources] = useState<DataSource[]>([]);
    const [folders, setFolders] = useState<Folder[]>([]);
    const [rootFolderUUID, setRootFolderUUID] = useState<string | null>(null);
    const [isFirstLogin, setIsFirstLogin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sharedReports, setSharedReports] = useState<EnrichedSharedReport[]>([]);

    const deleteSharedReport = useCallback(async (reportId: string) => {
        await sharedReportService.deleteSharedReport(reportId);
        setSharedReports(prev => prev.filter(r => r.report_id !== reportId));
    }, []);

    // Skip fetch on report page — no bootstrap data needed there
    const isReportPage = location.pathname === '/report';

    const fetchBootstrap = useCallback(async () => {
        if (isReportPage) return;
        if (USE_BACKEND) {
            if (!user?.email) {
                setError('User authentication required');
                return;
            }
            const result = await fetchDataAdmissionBootstrap(user.email);
            setDataSources(result.dataSources);
            setFolders(result.folders);
            setRootFolderUUID(result.rootFolderUUID);
            setIsFirstLogin(result.isFirstLogin);
        } else {
            const mockDataSources = await dataObjectService.getDataSources();
            setDataSources(mockDataSources);
        }
        setError(null);

        // Fetch shared reports in parallel — silently, no loading indicator
        sharedReportService.getSharedWithMe(50, 0).then(async (response) => {
            const enriched: EnrichedSharedReport[] = await Promise.all(
                response.reports.map(async (report) => {
                    try {
                        const detail = await sharedReportService.getSharedReport(report.report_id);
                        const raw = detail.raw_report as Record<string, unknown>;
                        const desc = raw.data_object_description as Record<string, unknown> | undefined;
                        const name = (raw.dataset_name as string)
                            || (raw.data_object_name as string)
                            || (raw.original_filename as string)
                            || (desc?.dataset_name as string)
                            || (desc?.table_name as string)
                            || undefined;
                        return { ...report, dataset_name: name };
                    } catch {
                        return { ...report };
                    }
                })
            );
            setSharedReports(enriched);
        }).catch(() => { /* silently ignore */ });
    }, [USE_BACKEND, user?.email, isReportPage]);

    // Initial bootstrap on mount
    useEffect(() => {
        if (isReportPage) {
            setLoading(false);
            setIsInitialLoad(false);
            return;
        }
        if (USE_BACKEND && !user?.id) return;

        let cancelled = false;
        (async () => {
            try {
                await fetchBootstrap();
            } catch (e: any) {
                if (!cancelled) {
                    const msg = e?.message || 'Failed to load data admission data';
                    setError(msg);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    setIsInitialLoad(false);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [user?.id, isReportPage]); // eslint-disable-line react-hooks/exhaustive-deps

    // Full refresh (shows loading state)
    const refreshData = useCallback(async () => {
        try {
            setLoading(true);
            await fetchBootstrap();
        } catch (e: any) {
            const msg = e?.message || 'Failed to load data admission data';
            setError(msg);
        } finally {
            setLoading(false);
            setIsInitialLoad(false);
        }
    }, [fetchBootstrap]);

    // Silent refresh (no loading flash)
    const silentRefresh = useCallback(async () => {
        try {
            await fetchBootstrap();
        } catch (e: any) {
            console.error('Silent refresh failed:', e);
        }
    }, [fetchBootstrap]);

    return (
        <DataAdmissionContext.Provider value={{
            dataSources,
            folders,
            rootFolderUUID,
            isFirstLogin,
            loading,
            isInitialLoad,
            error,
            refreshData,
            silentRefresh,
            setDataSources,
            setFolders,
            sharedReports,
            deleteSharedReport,
        }}>
            {children}
        </DataAdmissionContext.Provider>
    );
};

export const useDataAdmission = (): DataAdmissionContextType => {
    const context = useContext(DataAdmissionContext);
    if (context === undefined) {
        throw new Error('useDataAdmission must be used within a DataAdmissionProvider');
    }
    return context;
};
